import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { t } from '../../lib/i18n';

export interface TourStep {
  /** `data-tour` değerleri; görünen ilki vurgulanır. Hiçbiri yoksa kutucuk ekranın ortasında. */
  targets?: string[];
  text: string;
  /** Hedef bu düzende yoksa adımı atla (ör. masaüstünde olmayan Takvim sekmesi). */
  optional?: boolean;
}

/** Vurgulanan alanın çevresindeki boşluk (px). */
const PAD = 8;
/** Kutucuk ile vurgulanan alan arası (px). */
const GAP = 14;

interface Box {
  x: number;
  y: number;
  w: number;
  h: number;
}

function findTarget(step: TourStep | undefined): HTMLElement | null {
  for (const name of step?.targets ?? []) {
    for (const el of Array.from(document.querySelectorAll<HTMLElement>(`[data-tour="${name}"]`))) {
      const r = el.getBoundingClientRect();
      if (r.width > 0 && r.height > 0) return el;
    }
  }
  return null;
}

/** `from`dan başlayarak gösterilebilecek ilk adım (hedefi olmayan isteğe bağlı adımlar atlanır). */
function firstShowable(steps: TourStep[], from: number): number {
  let i = from;
  while (i < steps.length && steps[i].optional && !findTarget(steps[i])) i++;
  return i;
}

/**
 * Ürün turu: ekranı karartır, sıradaki öğeyi vurgular, yanında kısa bir
 * kutucuk açar. Düğme yok — ekranın herhangi bir yerine dokunmak bir sonraki
 * adıma geçer, son adımda tur kapanır. Klavyede Enter/Boşluk/→ ilerler,
 * Escape (Android geri tuşu) kapatır.
 */
export const ProductTour: React.FC<{ steps: TourStep[]; onDone: () => void }> = ({ steps, onDone }) => {
  const overlayRef = useRef<HTMLDivElement>(null);
  const [index, setIndex] = useState(() => firstShowable(steps, 0));
  const [hole, setHole] = useState<Box | null>(null);
  const [frame, setFrame] = useState({ w: 0, h: 0 });

  const measure = useCallback(() => {
    const overlay = overlayRef.current;
    if (!overlay) return;
    const o = overlay.getBoundingClientRect();
    setFrame({ w: o.width, h: o.height });
    const el = findTarget(steps[index]);
    if (!el) {
      setHole(null);
      return;
    }
    const r = el.getBoundingClientRect();
    setHole({ x: r.left - o.left - PAD, y: r.top - o.top - PAD, w: r.width + PAD * 2, h: r.height + PAD * 2 });
  }, [steps, index]);

  useLayoutEffect(() => {
    const el = findTarget(steps[index]);
    // Kaydırılan listedeki öğe görünür alanda değilse önce görünür yap, sonra ölç.
    el?.scrollIntoView({ block: 'nearest' });
    measure();
    const frameId = requestAnimationFrame(measure);
    return () => cancelAnimationFrame(frameId);
  }, [steps, index, measure]);

  useEffect(() => {
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, [measure]);

  const next = useCallback(() => {
    const target = firstShowable(steps, index + 1);
    if (target >= steps.length) onDone();
    else setIndex(target);
  }, [steps, index, onDone]);

  useEffect(() => {
    overlayRef.current?.focus();
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onDone();
      else if (event.key === 'Enter' || event.key === ' ' || event.key === 'ArrowRight') {
        event.preventDefault();
        next();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [next, onDone]);

  const step = steps[index];
  if (!step) return null;
  const isLast = firstShowable(steps, index + 1) >= steps.length;

  // Kutucuk: vurgulanan alanın altında ya da (yer yoksa) üstünde; yatayda ekrana sığacak şekilde.
  const width = Math.min(320, Math.max(0, frame.w - 32));
  let tipStyle: React.CSSProperties;
  if (hole) {
    const left = Math.min(Math.max(16, hole.x + hole.w / 2 - width / 2), Math.max(16, frame.w - width - 16));
    const below = frame.h - (hole.y + hole.h) >= hole.y;
    tipStyle = below
      ? { left, width, top: hole.y + hole.h + GAP }
      : { left, width, bottom: frame.h - hole.y + GAP };
  } else {
    tipStyle = { left: '50%', top: '50%', width, transform: 'translate(-50%, -50%)' };
  }

  const shown = steps.filter((s) => !s.optional || findTarget(s));
  const position = shown.indexOf(step);

  return (
    <div
      ref={overlayRef}
      tabIndex={-1}
      role="dialog"
      aria-modal="true"
      aria-label={t('Tanıtım turu', 'Product tour')}
      onClick={next}
      className="fixed inset-0 z-[70] cursor-pointer select-none outline-none"
    >
      {hole ? (
        <div
          aria-hidden="true"
          className="absolute rounded-[18px] ring-2 ring-[#46CBE2] pointer-events-none transition-all duration-300 ease-out"
          style={{ left: hole.x, top: hole.y, width: hole.w, height: hole.h, boxShadow: '0 0 0 9999px rgba(0, 0, 0, 0.66)' }}
        />
      ) : (
        <div aria-hidden="true" className="absolute inset-0 bg-black/[0.66]" />
      )}

      {/* Konum dış kapta, giriş animasyonu içte: animasyonun transform'u ortalamayı ezmesin. */}
      <div className="absolute" style={tipStyle}>
        <div
          key={index}
          aria-live="polite"
          className="animate-tour-in rounded-2xl bg-white text-[#111113] px-4 pt-3.5 pb-3 shadow-2xl shadow-black/40"
        >
          <p className="text-[16.5px] leading-[23px] font-semibold">{step.text}</p>
          <div className="mt-2.5 flex items-center justify-between gap-3">
            <span
              className="flex items-center gap-1"
              aria-label={t(`${position + 1}. adım, toplam ${shown.length}`, `Step ${position + 1} of ${shown.length}`)}
            >
              {shown.map((s, i) => (
                <span key={i} className={`h-1.5 rounded-full ${i === position ? 'w-4 bg-[#1DA4BA]' : 'w-1.5 bg-neutral-300'}`} />
              ))}
            </span>
            <span className="text-[12.5px] font-medium text-neutral-500">
              {isLast ? t('Bitirmek için dokun', 'Tap to finish') : t('Devam için dokun', 'Tap to continue')}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

/** Turun yalnızca bir kez gösterilmesi: kullanıcı başına bu cihazda. */
const tourKey = (userId: string) => `talknote-tur:${userId}`;

export function markTourPending(userId: string): void {
  try {
    if (localStorage.getItem(tourKey(userId)) !== 'done') localStorage.setItem(tourKey(userId), 'pending');
  } catch {
    // Depolama kapalıysa tur gösterilmez; uygulama etkilenmez.
  }
}

export function isTourPending(userId: string): boolean {
  try {
    return localStorage.getItem(tourKey(userId)) === 'pending';
  } catch {
    return false;
  }
}

export function markTourDone(userId: string): void {
  try {
    localStorage.setItem(tourKey(userId), 'done');
  } catch {
    // yok say
  }
}
